import React, { useEffect } from 'react';
import { Button, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import useMessage from 'src/app/hooks/useMessage';
import handleError from '@fuse/core/errorHandler';
import {
  useCreateProjectTagMutation,
  useUpdateProjectTagMutation,
} from 'src/app/services/projectTagsTypesApi';

const defaultValues = {
  name: '',
};

const ProjectTagsForm = ({ tempCat, isEdit, onClose }) => {
  const { t } = useTranslation('translations');
  const [updateCategory, { isLoading: isUpdateLoading }] = useUpdateProjectTagMutation();
  const [createCategory, { isLoading: isCreateLoading }] = useCreateProjectTagMutation();
  const { showMessage } = useMessage();

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues,
  });

  useEffect(() => {
    if (tempCat) {
      reset({
        name: tempCat.name || '',
      });
    } else {
      reset(defaultValues);
    }
  }, [tempCat, reset]);

  const handleClose = () => {
    onClose();
    reset(defaultValues);
  };

  const onSubmit = async (data) => {
    const trimmedData = {
      name: data.name.trim(),
    };

    const message = isEdit ? t('SuccessfullyUpdated') : t('SuccessfullyAdded');

    const promise = isEdit
      ? updateCategory({ id: tempCat.id, ...trimmedData })
      : createCategory(trimmedData);

    promise
      .unwrap()
      .then(() => {
        showMessage({ message: message, variant: 'success' });
        handleClose();
      })
      .catch(handleError);
  };

  const name = watch('name');

  const handleTrimOnChange = (field) => (e) => {
    setValue(field, e.target.value.trimStart(), { shouldValidate: true });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-16 pt-10 max-w-7xl">
      <Controller
        name="name"
        control={control}
        rules={{
          required: t('EmptyInput'),
          validate: (value) => value.trim().length > 0 || t('EmptyInput'),
        }}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('Title')}
            error={!!errors.name}
            helperText={errors.name?.message}
            onChange={(e) => {
              field.onChange(e);
              handleTrimOnChange('name')(e);
            }}
            value={field.value || ''}
          />
        )}
      />

      <Button
        disabled={isUpdateLoading || isCreateLoading || !isValid || !name.trim()}
        type="submit"
        className="w-full"
        variant="contained"
        color="secondary"
      >
        <span className="mx-8">{t('Save')}</span>
      </Button>
    </form>
  );
};

export default ProjectTagsForm;
